import type { YomiriPage } from '../types/extension.ts';
import { getSourceCacheScope } from './SourceSession.ts';

type Entry = { promise: Promise<Blob>; controller: AbortController; users: number; settled: boolean; blob?: Blob };
const entries = new Map<string, Entry>();
const MAX_BYTES = 64 * 1024 * 1024;
export const pageResourceKey = (page: YomiriPage) => JSON.stringify([getSourceCacheScope(), page.sourceId, page.imageUrl, page.encryptionKey, Object.entries(page.headers || {}).sort()]);
function trim() {
  let bytes = [...entries.values()].reduce((sum, entry) => sum + (entry.blob?.size || 0), 0);
  for (const [key, entry] of entries) {
    if (bytes <= MAX_BYTES && entries.size <= 60) break;
    if (!entry.users && entry.settled) { bytes -= entry.blob?.size || 0; entries.delete(key); }
  }
}
export function peekPageBlob(page: YomiriPage): Blob | undefined { return entries.get(pageResourceKey(page))?.blob; }
export function cachedPageBlob(page: YomiriPage, load: (signal: AbortSignal) => Promise<Blob>, signal?: AbortSignal): Promise<Blob> {
  if (signal?.aborted) return Promise.reject(signal.reason ?? new DOMException('Cancelado', 'AbortError'));
  const key = pageResourceKey(page);
  let entry = entries.get(key);
  if (!entry) {
    const controller = new AbortController();
    entry = { controller, users: 0, settled: false, promise: Promise.resolve(undefined as unknown as Blob) };
    const created = entry;
    entry.promise = Promise.resolve().then(() => load(controller.signal)).then(blob => {
      created.blob = blob; created.settled = true; trim(); return blob;
    }, error => { created.settled = true; if (entries.get(key) === created) entries.delete(key); throw error; });
    entries.set(key, entry);
  }
  const current = entry;
  entries.delete(key); entries.set(key, current); current.users++;
  let unsubscribe = () => {};
  return new Promise<Blob>((resolve, reject) => {
    const abort = () => reject(signal?.reason ?? new DOMException('Cancelado', 'AbortError'));
    signal?.addEventListener('abort', abort, { once: true });
    unsubscribe = () => signal?.removeEventListener('abort', abort);
    current.promise.then(resolve, reject);
  }).finally(() => {
    unsubscribe();
    // Independent consumers share the transfer; the last cancellation stops it.
    current.users--;
    if (!current.users && !current.settled) { if (entries.get(key) === current) entries.delete(key); current.controller.abort(); }
    trim();
  });
}
